import { useEffect, useRef, useState } from "react";
import { getHost } from "../services/api";

interface LogViewerProps {
    userId: string;
    isOpen: boolean;
    onClose: () => void;
}

const LogViewer: React.FC<LogViewerProps> = ({ userId, isOpen, onClose }) => {
    const [logs, setLogs] = useState<string>("");
    const [fontSize, setFontSize] = useState<number>(14);
    const [searchTerm, setSearchTerm] = useState("");
    const [matches, setMatches] = useState<number[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

    const logBoxRef = useRef<HTMLDivElement | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);
    const isUserScrollingRef = useRef(false);


    useEffect(() => {
        if (!isOpen) return;

        const eventSource = new EventSource(`${getHost()}stream_logs?userId=${userId}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            console.log("Received log:", String(event.data).length);
            setLogs((prev) => prev + event.data + "\n");

            if (String(event.data).includes("please close this window %% 2F3521506821326974.") && String(event.data).length < 100){
                eventSource.close()
            }
        };

        eventSource.onerror = (err) => {
            console.error("SSE error:", err);
            setLogs((prev) => prev + "\n[Connection lost. Trying to reconnect...]\n");
        };

        eventSource.onopen = () => {
            console.log("SSE connection opened");
            setLogs((prev) => prev + "\n[Connection established]\n");
        };

        return () => {
            eventSource.close();
        };
    }, [userId, isOpen]);

    useEffect(() => {
        if (logBoxRef.current) {
            logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
        }
    }, [logs]);

    useEffect(() => {
        if (!searchTerm) {
            setMatches([]);
            setCurrentMatchIndex(0);
            return;
        }

        const allLines = logs.split("\n");
        const found = allLines
            .map((line, idx) =>
                line.toLowerCase().includes(searchTerm.toLowerCase()) ? idx : -1
            )
            .filter((idx) => idx !== -1);

        setMatches(found);
        setCurrentMatchIndex(0);
    }, [searchTerm, logs]);

    useEffect(() => {
        if (matches.length === 0 || !logBoxRef.current) return;

        const container = logBoxRef.current;
        const lineHeight = fontSize + 4; // Adjust based on your CSS
        const matchPosition = matches[currentMatchIndex] * lineHeight;
        const containerHeight = container.clientHeight;

        container.scrollTop = matchPosition - containerHeight / 2 + lineHeight / 2;
    }, [currentMatchIndex, matches, fontSize]);

    useEffect(() => {
        const box = logBoxRef.current;
        if (!box) return;
        handleScroll()

    }, [matches, fontSize]);

    const goToNext = () => {
        if (matches.length === 0) return;
        setCurrentMatchIndex((prev) => (prev + 1) % matches.length);
    };

    const goToPrevious = () => {
        if (matches.length === 0) return;
        setCurrentMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
    };

    const handleScroll = () => {
        if (!logBoxRef.current || matches.length === 0) return;

        const scrollTop = logBoxRef.current.scrollTop;
        const visibleHeight = 550;
        console.log(visibleHeight)
        const centerOffset = scrollTop + visibleHeight / 2;

        const approxLineHeight = fontSize + 4;
        const centerLineIndex = Math.floor(centerOffset / approxLineHeight);

        // Find the closest match to the center line
        let closestMatch = matches[0];
        let minDistance = Math.abs(closestMatch - centerLineIndex);
        for (const idx of matches) {
            const dist = Math.abs(idx - centerLineIndex);
            if (dist < minDistance) {
                closestMatch = idx;
                minDistance = dist;
            }
        }

        setCurrentMatchIndex(matches.indexOf(closestMatch));
    };

    const handleClose = () => {
        setLogs("")
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-2xl max-w-9xl w-full p-6 text-white overflow-y-auto max-h-[95vh]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-yellow-300">
                        Live Logs for User: {userId}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-white/80 hover:text-red-400 text-2xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 items-center mt-4 text-white text-sm">
                    <label>Font Size:</label>
                    <input
                        type="number"
                        min={6}
                        max={30}
                        value={fontSize}
                        onChange={(e) => setFontSize(parseInt(e.target.value))}
                        className="bg-black/50 text-white px-2 py-1 rounded border border-white/30 w-20"
                    />
                    <label>Search:</label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault(); // prevent scrolling on Space
                                goToNext();
                            }
                        }}
                        className="bg-black/50 text-white px-2 py-1 rounded border border-white/30 w-48"
                        placeholder="Find text..."
                    />
                    <button
                        onClick={goToPrevious}
                        className="bg-gray-700 px-2 py-1 rounded hover:bg-gray-600"
                    >
                        ⬆ Prev
                    </button>
                    <button
                        onClick={goToNext}
                        onKeyDown={goToNext}
                        className="bg-gray-700 px-2 py-1 rounded hover:bg-gray-600"
                    >
                        ⬇ Next
                    </button>
                    <span className="ml-2">
                        {matches.length > 0
                            ? `Match ${currentMatchIndex + 1} of ${matches.length}`
                            : "No matches"}
                    </span>
                </div>

                <div
                    ref={logBoxRef}
                    className="bg-black/70 text-green-400 font-mono p-4 h-[65vh] overflow-auto rounded shadow-inner border border-green-500/20 mt-4"
                    style={{ fontSize: `${fontSize}px` }}
                >
                    <pre className="whitespace-pre-nowrap">
                        {logs.split("\n").map((line, idx) => (
                            <span key={idx} className="block">
                                {matches[currentMatchIndex] === idx ? (
                                    <mark className="bg-yellow-300 text-black px-1 rounded">
                                        {line}
                                    </mark>
                                ) : (
                                    line
                                )}
                            </span>
                        ))}
                    </pre>
                </div>
            </div>
        </div>
    );
};

export default LogViewer;
